/**
 * Makes one element reachable by the remote.
 *
 * Returns the ref to attach and whether it is currently focused, so a component draws its
 * own focus treatment rather than relying on `:focus` — the platform's native focus ring
 * is inconsistent across panels and invisible at TV viewing distance.
 */
import { useCallback, useEffect, useId, useRef } from 'react';
import { useFocus } from './FocusContext';

export interface FocusableOptions {
  /** Stable id. Defaults to a generated one, which is fine unless focus must be restored. */
  id?: string;
  /** Rail or screen this belongs to, for focus restoration. */
  group?: string;
  onSelect?: () => void;
  disabled?: boolean;
  /** Takes focus on mount, overriding "first registered wins". */
  autoFocus?: boolean;
}

export function useFocusable<T extends HTMLElement = HTMLElement>(options: FocusableOptions = {}) {
  const generatedId = useId();
  const id = options.id ?? generatedId;
  const elementRef = useRef<T | null>(null);
  const { register, focusedId, focus } = useFocus();

  // Held in a ref so a new closure on every render does not re-register the element —
  // re-registration would reset focus on every parent re-render, which on a rail that
  // updates as it loads means focus jumping back to the first card repeatedly.
  const onSelectRef = useRef(options.onSelect);
  onSelectRef.current = options.onSelect;

  /**
   * A callback ref rather than a plain `useRef`, and the two attributes below are why.
   *
   * They used to be written in the effect underneath, which is one microtask too late.
   * `AutoFocusables` watches its subtree with a `MutationObserver` and skips anything
   * carrying `data-focusable`; observer callbacks are microtasks, so they run as soon as
   * the commit's call stack empties — **before** React gets round to passive effects. Any
   * focusable that mounted after the first paint therefore looked unclaimed, and the
   * observer registered it a second time under a generated `auto:N` id.
   *
   * That is every card on Home and on the three libraries, because their rows arrive from
   * a fetch. Measured on the movie library: 129 cards, 129 of them registered twice. Three
   * things followed from it, none of them obvious from the screen:
   *
   *   - The `auto:N` entry is the one focus lands on, so `focusedId` never matched the id
   *     the card knows itself by. `is-focused` was therefore never applied: no `--tv-scale`
   *     and no ring on the artwork, only the generic whole-element outline that
   *     `.tv .tv__auto :focus` draws for the screens that have no design of their own.
   *   - `move()` scored 258 rectangles per key press instead of 129, on a 32-bit ARM panel.
   *   - The duplicate carries `group: 'auto'`, so `restoreGroupFocus` could never return
   *     to the rail you left.
   *
   * A callback ref runs during the commit, synchronously, before the stack unwinds — so
   * the attribute is there by the time the observer looks. Nothing reads `.current` from
   * outside this hook, so the change is invisible to every caller.
   */
  const ref = useCallback((node: T | null) => {
    elementRef.current = node;
    if (!node) return;
    // Needed for the DOM focus that follows the logical focus; without it `.focus()` is
    // a no-op on a div and the platform keeps focus wherever it was.
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
    // Marks the element as explicitly claimed, so `AutoFocusables` leaves it and its
    // children alone.
    node.setAttribute('data-focusable', '1');
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const unregister = register({
      id,
      group: options.group,
      element,
      disabled: options.disabled,
      onSelect: () => onSelectRef.current?.(),
    });
    return unregister;
  }, [id, options.group, options.disabled, register]);

  useEffect(() => {
    if (options.autoFocus) focus(id);
    // Deliberately only on mount: re-running would steal focus back every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);

  /**
   * `elementRef` is the node itself, for the callers that need it — the search field asks
   * the platform for its keyboard by calling `.focus()` on it. It is separate from `ref`
   * because `ref` is now a function and cannot carry a `.current`.
   */
  return { ref, elementRef, focused: focusedId === id, id };
}
